import React, { useMemo } from "react";
import { Box, Chip, Stack, Typography, Grid } from "@mui/material";
import LGModalCrud from "../LGCrud/LGModalCrud";
import {
  cancelPurchaseOrder,
  exportPO,
  getPurchaseOrderList,
} from "../../api/purchaseOrders";
import { useSnack } from "../../services/SnakbarProvider";
import {
  currencyFormatterMXN,
  dateFormatter,
} from "../../services/valueFormatters";
import { handleDownload as downloadPDF } from "../../services/PDFHandler.ts";
import POUpdateForm from "./POUpdateForm";
import PurchaseOrderForm from "./PurchaseOrderForm";
import PurchaseOrderAttachmentsTable from "./PurchaseOrderAttachmentsTable";
import { Divider } from "@mui/material";

const DEFAULT_VISIBLE_COLUMNS = [
  "code",
  "arrivalDate",
  "providerCode",
  "total",
  "currency",
  "status",
  "items",
];

const setStatusColor = (statusCode) => {
  switch (statusCode) {
    case "PO_SIN_APROBAR" || "PO_PARTIALLY_DELIVERED" || "PO_OVERDELIVERED":
      return "warning";
    case "PO_APROBADA" || "PO_DELIVERED":
      return "success";
    case "PO_CANCELADA":
      return "error";
  }
};

export default function PurchaseOrderTable({ companyId }) {
  const { openSnack } = useSnack();

  const columns = useMemo(
    () => [
      {
        field: "code",
        headerName: "Folio",
        flex: 0.2,
      },
      {
        field: "arrivalDate",
        headerName: "Fecha de entrega",
        flex: 0.2,
        ...dateFormatter,
      },
      {
        field: "arrivalEta",
        headerName: "Fecha estimada de entrega",
        flex: 0.2,
        ...dateFormatter,
      },
      {
        field: "providerCode",
        headerName: "Proveedor",
        valueGetter: (value, row) => {
          return row.provider.code || "-";
        },
        flex: 0.3,
      },
      {
        field: "total",
        headerName: "Total",
        flex: 0.2,
        ...currencyFormatterMXN,
      },
      {
        field: "currency",
        headerName: "Moneda",
        flex: 0.1,
        valueGetter: (value, row) => {
          return row.currency.code === "EUR"
            ? "€"
            : row.currency.symbol || "-";
        },
      },
      {
        field: "status",
        headerName: "Estatus",
        flex: 0.2,
        renderCell: (params) => (
          <Box
            mt={2}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Chip
              size="small"
              label={params.row.status.name.toUpperCase()}
              color={setStatusColor(params.row.status.code)}
            />
          </Box>
        ),
      },
      {
        field: "people",
        headerName: "Responsables",
        flex: 0.35,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const { createdBy, approvedBy, requestedFrom, status } = params.row;
          return (
            <Stack
              direction="column"
              spacing={0.25}
              justifyContent="center"
              sx={{ height: "100%", py: 1 }}
            >
              <Typography variant="caption">
                Creado por: {createdBy || "-"}
              </Typography>
              {status?.code === "PO_APROBADA" && (
                <Typography variant="caption">
                  Autorizado por: {approvedBy || "-"}
                </Typography>
              )}
              <Typography variant="caption">
                Solicitado por: {requestedFrom || "-"}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "items",
        headerName: "Progreso de entrega",
        flex: 0.5,
        renderCell: (params) => {
          return (
            <Box sx={{ width: "100%", maxHeight: 200, overflow: "auto" }}>
              <Stack direction="column" spacing={1}>
                {params.value.map((item, index) => {
                  const delivered = item.quantityDelivered ?? 0;
                  const total = item.quantity;
                  const progress = total > 0 ? (delivered / total) * 100 : 0;
                  const progressColor =
                    progress > 100 ? "#ffAAAA" : "#a5c8f0";
                  return (
                    <Grid
                      container
                      key={index}
                      spacing={1}
                      sx={{ width: "100%" }}
                    >
                      <Grid item xs={6}>
                        <Stack spacing={0}>
                          {item.product?.code && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ lineHeight: 1.2 }}
                            >
                              {item.product.code}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ mb: 0.5 }}>
                            {item.concept}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={4}>
                        <Box
                          sx={{
                            height: 20,
                            bgcolor: "#edf4fb",
                            borderRadius: 1,
                            position: "relative",
                            overflow: "hidden",
                            mb: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: `${progress}%`,
                              height: "100%",
                              bgcolor: progressColor,
                              transition: "width .5s ease-in-out",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              color: "#1976d2",
                              fontWeight: "bold",
                              textShadow: "0 0 2px white",
                            }}
                          >
                            {`${delivered}/${total}`}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={2}>
                        {item.workOrderId && (
                          <Chip
                            size="small"
                            label={item.workOrderId}
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Grid>
               
                    </Grid>
                  );
                })}
              </Stack>
            </Box>
          );
        },
      },
    ],
    []
  );

  const columnVisibilityModel = useMemo(
    () =>
      columns.reduce((acc, column) => {
        acc[column.field] = DEFAULT_VISIBLE_COLUMNS.includes(column.field);
        return acc;
      }, {}),
    [columns]
  );

  const handleFileDownload = async (id) => {
    try {
      const response = await exportPO(id, true);
      downloadPDF(
        response,
        "ordencompra",
        "No se pudo generar el PDF, verifica que el proveedor tenga contacto y dirección",
        (error) => {
          openSnack(
            "No se pudo generar el PDF, verifica que el proveedor tenga contacto y dirección",
            "error"
          );
        }
      );
    } catch (e) {
      openSnack(
        "No se pudo generar el PDF, verifica que el proveedor tenga contacto y dirección",
        "error"
      );
    }
  };

  return (
    <Box width={"100%"}>
      <LGModalCrud
        props={{
          actions: ["create", "update", "download", "delete", "detail"],
          data: { companyId },
          fetchFunction: getPurchaseOrderList,
          deleteFunction: cancelPurchaseOrder,
          downloadFunction: handleFileDownload,
          detailMenu: [
            {
              label: "Duplicar",
              handler: () => {},
              header: "Duplicar Órden de compra",
              body: (
                <PurchaseOrderForm duplicate={true} companyId={companyId} />
              ),
            },
            {
              label: "Documentos",
              handler: (rowData) => {
                return rowData;
              },
              header: "Documentos de la Orden de Compra",
              body: <PurchaseOrderAttachmentsTable companyId={companyId} />,
            },
          ],
          columns,
          addModalData: {
            header: "Agregar Órden de compra",
            body: <PurchaseOrderForm companyId={companyId} />,
          },
          editModalData: {
            header: "Editar Órden de compra",
            body: <POUpdateForm />,
          },
          dataGridProps: {
            initialState: {
              columns: {
                columnVisibilityModel,
              },
            },
          },
        }}
      />
    </Box>
  );
}
